import { Link } from "@/lib/router-compat";
import { optimizedImg, PLACEHOLDER_URL } from "@/lib/imageUtils";
import type { RVUnit } from "@/hooks/useInventory";

import CardImage from "@/components/inventory/CardImage";
import GetLowestPrice from "@/components/inventory/GetLowestPrice";
import { ActionForm } from "@/components/inventory/UnitActions";
import { DollarSign } from "lucide-react";
import AdminEditButton from "@/components/inventory/AdminEditButton";
import WishlistButton from "@/components/inventory/WishlistButton";
import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { rvPlaceholder } from "@/lib/rvPlaceholders";
import { unitPhotos, isPlaceholderPhoto } from "@/lib/rvPhotoPolicy";

/** Context for user-level pause toggle on the inventory page */
const AutoCyclePausedContext = createContext(false);
export const AutoCyclePausedProvider = AutoCyclePausedContext.Provider;

function fmt(n: number | null) {
  return n ? n.toLocaleString() : "—";
}

function formatFeet(feet: number | null): string | null {
  if (!feet) return null;
  const ft = Math.floor(feet);
  let inches = Math.round((feet - ft) * 100);
  if (inches > 11) inches = Math.round((feet - ft) * 12);
  return inches > 0 ? `${ft}' ${inches}"` : `${ft}'`;
}

/** Shared hook – cached globally so all cards share one query */
export function useAutoCycleSetting() {
  return useQuery({
    queryKey: ["site-setting", "inventory_image_auto_cycle"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "inventory_image_auto_cycle")
        .maybeSingle();
      if (!data) return { enabled: false, interval: 3 };
      const val = data.value as any;
      return {
        enabled: val?.enabled ?? false,
        interval: val?.interval ?? 3,
      };
    },
    staleTime: 60_000,
  });
}

export default function RVCard({ rv, priority, noCycle }: { rv: RVUnit; priority?: boolean; noCycle?: boolean }) {
  const fallback = rvPlaceholder(rv.rv_type);
  const photos = unitPhotos(rv.images);
  const baseImages = photos.length ? photos : [fallback];
  // Force floorplan to be the 2nd image when available, even if it already exists later in the list.
  const rawImages = rv.floorplan_image
    ? [baseImages[0] ?? fallback, rv.floorplan_image, ...baseImages.slice(1).filter((url) => url !== rv.floorplan_image)]
    : baseImages;
  const displayPrice = rv.sale_price ?? rv.price;
  const [hasTour, setHasTour] = useState(false);
  useEffect(() => {
    import("@/data/matterportTours").then((m) => {
      setHasTour(!!m.findMatterportTour(rv.model, rv.trim, rv.title));
    });
  }, [rv.model, rv.trim, rv.title]);

  const { data: autoCycle } = useAutoCycleSetting();
  const userPaused = useContext(AutoCyclePausedContext);

  const [imgIdx, setImgIdx] = useState(0);
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const images = rawImages.filter((u) => !failedUrls.has(u));
  const hasFloorplan = !!rv.floorplan_image && images.includes(rv.floorplan_image);
  const shouldCycle = (autoCycle?.enabled || hasFloorplan) && images.length > 1 && !userPaused && !noCycle;

  // Auto-cycle
  useEffect(() => {
    if (!shouldCycle) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const ms = (autoCycle?.interval ?? 3) * 1000;
    timerRef.current = setInterval(() => {
      setImgIdx((prev) => (prev + 1) % images.length);
    }, ms);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [shouldCycle, images.length, autoCycle?.interval]);

  useEffect(() => { if (userPaused) setImgIdx(0); }, [userPaused]);
  useEffect(() => { setImgIdx(0); setFailedUrls(new Set()); }, [rv.id]);

  const safeIdx = images.length > 0 ? imgIdx % images.length : 0;
  const currentImg = images.length > 0 ? (images[safeIdx] ?? fallback) : fallback;

  const handleImgError = useCallback(() => {
    const failedSrc = images[safeIdx];
    if (!failedSrc) return;
    setFailedUrls((prev) => {
      const next = new Set(prev);
      next.add(failedSrc);
      return next;
    });
    setImgIdx(0);
  }, [images, safeIdx]);

  const isSold = rv.status === "sold";
  const savings = rv.price && rv.sale_price && rv.sale_price < rv.price ? rv.price - rv.sale_price : 0;

  return (
    <Link
      to={`/product/${rv.slug}`}
      className="block w-full max-w-[350px] mx-auto rounded-xl overflow-hidden bg-card shadow-xs"
    >
      {/* IMAGE WRAPPER */}
      <div className="relative">
        <CardImage
          src={currentImg}
          alt={!photos.length && currentImg === fallback ? "Photos coming soon — representative graphic, not a unit photo" : rv.status === "available" ? `${rv.title} - In Stock` : rv.title}
          placeholder={currentImg === fallback || isPlaceholderPhoto(currentImg)}
          onError={handleImgError}
          priority={priority}
        />

        {/* BADGES */}
        <div className="absolute left-2 top-2 flex flex-wrap gap-1.5">
          {isSold && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-destructive text-destructive-foreground">
              Sold
            </div>
          )}
          {rv.status === "coming_soon" && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-amber-800 text-white">
              Coming Soon
            </div>
          )}
          {rv.status === "pending" && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-orange-700 text-white">
              Hold
            </div>
          )}
          {!isSold && rv.condition === "new" && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary text-primary-foreground">
              New
            </div>
          )}
          {!isSold && rv.condition === "used" && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground">
              Used
            </div>
          )}
          {rv.is_special && rv.special_label && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-accent text-accent-foreground">
              {rv.special_label}
            </div>
          )}
          {hasTour && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-primary/90 text-primary-foreground">
              3D Tour
            </div>
          )}
        </div>

        {/* SAVE BADGE */}
        {savings > 0 && (
          <div className="absolute right-2 top-2 rounded-sm bg-primary px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
            SAVE ${fmt(savings)}
          </div>
        )}

        {/* WISHLIST + ADMIN BUTTONS */}
        <WishlistButton stockNumber={rv.stock_number} className="absolute right-2 bottom-2" />
        <AdminEditButton stockNumber={rv.stock_number} className="absolute left-2 bottom-2" />
        {rv.status === "available" && (
          <div className="absolute left-2 bottom-10 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-green-800 text-white">
            In Stock
          </div>
        )}
      </div>

      {/* CONTENT AREA */}
      <div className="p-4 space-y-2">
        {/* TITLE */}
        <h3 className="text-base font-semibold leading-tight text-foreground line-clamp-2 min-h-[2.5rem]">
          {rv.title}
        </h3>
        {/* STOCK # */}
        <p className="text-xs text-muted-foreground font-mono">
          Stock # {rv.stock_number}
        </p>

        {/* SPECS */}
        <div className="flex flex-nowrap overflow-hidden gap-3 text-xs text-muted-foreground min-h-4">
          {rv.sleeps && <span>{rv.sleeps} Sleeps</span>}
          {rv.weight_lbs && <span>{fmt(rv.weight_lbs)} LBS</span>}
          {rv.length_ft && <span>{formatFeet(rv.length_ft)} Long</span>}
        </div>

        {/* PRICE */}
        {isSold ? (
          <div className="text-sm font-semibold text-destructive">
            This unit has been sold.
          </div>
        ) : (
          <>
            <div className="text-lg font-bold text-primary">
              ${fmt(displayPrice)}
            </div>
            <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <GetLowestPrice
                title={rv.title}
                stockNumber={rv.stock_number}
                year={rv.year}
                make={rv.make}
                model={rv.model}
                variant="compact"
                className="w-full"
              />
            </div>
          </>
        )}
      </div>
    </Link>
  );
}
